import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withCtx as _withCtx, createVNode as _createVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveDynamicComponent as _resolveDynamicComponent, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "task-connections" }
const _hoisted_2 = ["textContent"]
const _hoisted_3 = ["textContent"]

import { computed, ComputedRef, inject, PropType, ref } from 'vue';
import {
  Tabs,
  Tab,
  TirPmButton,
  TirPmButtonVariantEnum,
  TirPmButtonSizeEnum,
  PopoverPositionEnum,
  TirPmScrollbar,
  TirPmTabsVariantEnum,
} from 'tir-components';
import { CurrentUser, UserRoleEnum } from 'tir-pm-user-core';
import { PlusIcon } from 'tir-style-system/icons/outline';
import { QueueRelationModel, SubtaskModel } from '@/components/task-forms/models';
import { TaskQueueSearchDropdown, TaskSubtasksSearchDropdown } from '@/components/task-forms';
import { QueuesList, SubtasksList } from './components';
import { AdapterService } from './adapter-service';
import { TaskModel } from '../task-drawer/models';
import GetAccessCurrentUserInQueueTaskByIdResponseModel from '../task-detail/models/GetAccessCurrentUserInQueueTaskByIdResponseModel';
import { EmployeeQueueTaskAccessEnum } from '../task-detail/enums';

/**API сервис */

export default /*#__PURE__*/_defineComponent({
  __name: 'task-relations',
  props: {
  /**Редактируемая задача */
  editTaskInfo: { type: Object as PropType<TaskModel> },
  /**id проекта */
  projectId: { type: Number, required: true },
  /**Функция для поиска подзадач */
  tasksSearchFunction: { type: Function },
  /**Id задачи */
  taskId: { type: Number },
  /**Id текущей очереди */
  queueId: { type: String },
  /**Список очередей задачи */
  queues: { type: Array as PropType<QueueRelationModel[]> },
  /**Подзадачи */
  subtasks: { type: Array as PropType<SubtaskModel[]> },
  /**Режим редактирования */
  isEdit: { type: Boolean, default: true },
  /**Внутри шторки */
  inDrawer: { type: Boolean, default: false },
  /**Доступы юзера к очереди */
  userAccess: { type: Object as PropType<GetAccessCurrentUserInQueueTaskByIdResponseModel> },
},
  emits: ["createdSubtask", "updatedSubtask", "attachSubtask", "removeSubtask", "attachQueue", "subtaskItem", "removeSubtaskItem"],
  setup(__props, { expose: __expose, emit }) {

const props = __props;

const apiService = new AdapterService();

/**Параметры */

/**События */


/**Владелец */
const isOwner = computed(() => CurrentUser.Roles.includes(UserRoleEnum.Owner));
/**Админ проекта */
const isProjectAdmin = inject('isAdminProject') as ComputedRef<boolean>;

/**Текущий таб */
const currentTab = ref(0);
/**Ссылка на скроллбар */
const $scrollbar = ref<InstanceType<typeof TirPmScrollbar>>();
/**Ссылка на подзадачи */
const $subtasks = ref<InstanceType<typeof SubtasksList>>();
/**Ссылка на очереди */
const $queues = ref<InstanceType<typeof QueuesList>>();
/**id выбранной очереди */
const selectedQueueId = ref();

/**Последний элемент подзадачи */
const lastElementSubtask = computed(() => {
  return $subtasks.value.subtask.length - 1;
});
/**Подзадача */
const subtaskElement = computed(() => {
  return $subtasks.value.subtask[lastElementSubtask.value].subtaskItem;
});

/**Открыт таб подзадач */
const isSubtasksTab = computed(() => currentTab.value == 0);
/**Компонент выпадающего списка */
const dropdownComponent = computed(() =>
  isSubtasksTab.value ? TaskSubtasksSearchDropdown : TaskQueueSearchDropdown,
);
/**Отображать кнопку добавления */
const isVisibleAddButton = computed(() => {
  // Для очередей
  if (currentTab.value === 1) {
    // Добавлять очереди могут владелец, админ проекта
    return isOwner.value || isProjectAdmin.value;
  }
  return true;
});
/**Функция поиска очереди/задачи */
const searchFunction = computed(() =>
  isSubtasksTab.value ? props.tasksSearchFunction : apiService.getQueues.bind(apiService),
);
/**Id уже добавленных задач */
const ignoreTasks = computed(() => props.subtasks.map((s) => s.Id));
/**Отображение кнопки добавления подзадач */
const isShowAddSubtaskButton = computed(
  () =>
    props.userAccess.IsOwner ||
    props.userAccess.IsNamespaceAdmin ||
    props.userAccess.IsProjectAdmin ||
    props.userAccess.EmployeeQueueTaskAccesses.includes(EmployeeQueueTaskAccessEnum.CreateTask),
);

/**Спуститься до задачи */
const scrollToLastElement = (element: HTMLElement) => {
  const scrollContainer = $scrollbar.value?.$scroll;

  const currentScrollTop = scrollContainer.scrollTop;
  const targetScrollTop =
    currentScrollTop +
    element.getBoundingClientRect().top -
    scrollContainer.getBoundingClientRect().top -
    20;

  scrollContainer.scrollTo({
    top: targetScrollTop,
    behavior: 'smooth',
  });
};
/**Удалить подзадачу */
const removeSubtask = (taskId: number) => {
  const removeSubtaskIndex = props.subtasks.findIndex((q) => q.Id == taskId);
  if (removeSubtaskIndex == -1) return;

  emit('removeSubtask', taskId);
  emit('removeSubtaskItem');
  props.queues.splice(removeSubtaskIndex, 1);
};
/**Удалить задачу из очереди */
const removeQueue = (queueId: string) => {
  const removeQueueIndex = props.queues.findIndex((q) => q.Id == queueId);
  if (removeQueueIndex == -1) return;

  apiService.removeQueueRelation(props.taskId, queueId);
  props.queues.splice(removeQueueIndex, 1);
};
/**При выборе задачи/очереди */
const onSelectItem = async (data: number | QueueRelationModel) => {
  if (isSubtasksTab.value && typeof data == 'number') {
    emit('attachSubtask', data);
    emit('subtaskItem');
  } else {
    emit('attachQueue');
    data = data as QueueRelationModel;
    await apiService.addQueueRelation(props.taskId, data.Id);
    props.queues.push(data);
    selectedQueueId.value = data.Id;
  }
};

__expose({
  subtaskElement,
  scrollToLastElement,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(Tabs), {
      class: "task-connections_tabs",
      modelValue: currentTab.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((currentTab).value = $event)),
      variant: _unref(TirPmTabsVariantEnum).Secondary
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(Tab), {
          label: "Подзадачи",
          val: 0,
          "current-tab": currentTab.value
        }, {
          default: _withCtx(() => [
            _createElementVNode("span", {
              textContent: _toDisplayString(__props.subtasks?.length || 0),
              class: "task-connections_count"
            }, null, 8, _hoisted_2)
          ]),
          _: 1
        }, 8, ["current-tab"]),
        _createVNode(_unref(Tab), {
          label: "Очереди",
          val: 1,
          "current-tab": currentTab.value
        }, {
          default: _withCtx(() => [
            _createElementVNode("span", {
              textContent: _toDisplayString(props.queues?.length || 0),
              class: "task-connections_count"
            }, null, 8, _hoisted_3)
          ]),
          _: 1
        }, 8, ["current-tab"])
      ]),
      _: 1
    }, 8, ["modelValue", "variant"]),
    (isVisibleAddButton.value && __props.isEdit)
      ? (_openBlock(), _createBlock(_resolveDynamicComponent(dropdownComponent.value), {
          key: 0,
          class: "task-connections_dropdown",
          editTaskInfo: __props.editTaskInfo,
          staticScrollHeight: "",
          inDrawer: __props.inDrawer,
          searchFunction: searchFunction.value,
          "is-disabled": !__props.isEdit,
          "ignore-tasks": ignoreTasks.value,
          "custom-popover-position": _unref(PopoverPositionEnum).Top,
          "task-id": __props.taskId,
          "task-queue-id": __props.queueId,
          "allow-create-subtask": !!__props.taskId,
          onOnSelect: onSelectItem,
          onOnCreatedSubtask: _cache[1] || (_cache[1] = ($event: any) => (emit('createdSubtask', $event))),
          onOnUpdatedSubtask: _cache[2] || (_cache[2] = ($event: any) => (emit('updatedSubtask', $event)))
        }, {
          target: _withCtx(() => [
            (isShowAddSubtaskButton.value)
              ? (_openBlock(), _createBlock(_unref(TirPmButton), {
                  key: 0,
                  class: "task-connections_add",
                  variant: _unref(TirPmButtonVariantEnum).LinkState,
                  size: _unref(TirPmButtonSizeEnum).Small
                }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(PlusIcon)),
                    _createTextVNode(" Добавить ")
                  ]),
                  _: 1
                }, 8, ["variant", "size"]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }, 40, ["editTaskInfo", "inDrawer", "searchFunction", "is-disabled", "ignore-tasks", "custom-popover-position", "task-id", "task-queue-id", "allow-create-subtask"]))
      : _createCommentVNode("", true),
    (currentTab.value == 0)
      ? (_openBlock(), _createBlock(_unref(SubtasksList), {
          key: 1,
          ref_key: "$subtasks",
          ref: $subtasks,
          projectId: __props.projectId,
          subtasks: __props.subtasks,
          queueId: __props.queueId,
          onRemove: removeSubtask,
          onUpdatedSubtask: _cache[3] || (_cache[3] = ($event: any) => (emit('updatedSubtask', $event))),
          editing: __props.isEdit
        }, null, 8, ["projectId", "subtasks", "queueId", "editing"]))
      : (currentTab.value == 1)
        ? (_openBlock(), _createBlock(_unref(QueuesList), {
            key: 2,
            ref_key: "$queues",
            ref: $queues,
            queues: __props.queues,
            projectId: __props.projectId,
            scrollbar: $scrollbar.value,
            selectedQueueId: selectedQueueId.value,
            onRemove: removeQueue,
            editing: __props.isEdit
          }, null, 8, ["queues", "projectId", "scrollbar", "selectedQueueId", "editing"]))
        : _createCommentVNode("", true)
  ]))
}
}

})